/**
 * activityLogger.ts
 *
 * Lightweight utility for writing to the activity_log table.
 * Call `logActivity(...)` from any mutation hook after a successful action.
 *
 * Uses fire-and-forget — the caller never needs to await or handle errors from
 * this function; a warning is printed to the console on failure.
 */

import { supabase } from "@/lib/supabase";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ActivityVisibility = "private" | "unit" | "building" | "admin";

export interface LogActivityParams {
  orgId: string;
  userId?: string | null;
  actionType: ActivityActionType;
  entityType?: ActivityEntityType;
  entityId?: string | null;
  visibility?: ActivityVisibility;
  metadata?: Record<string, unknown>;
}

// ─── Supported action types ───────────────────────────────────────────────────

export type ActivityActionType =
  // Host lifecycle
  | "host_registered"
  | "host_approved"
  | "host_rejected"
  | "host_suspended"
  | "host_invited"
  // Booking lifecycle
  | "booking_created"
  | "booking_approved"
  | "booking_rejected"
  | "booking_blocked"
  | "booking_cancelled"
  | "booking_completed"
  // Unit management
  | "unit_added"
  | "unit_updated"
  | "unit_deleted"
  // Documents
  | "document_uploaded"
  | "document_deleted"
  | "document_approved"
  | "document_rejected"
  // Disputes
  | "dispute_filed"
  | "dispute_updated"
  | "dispute_resolved"
  // Compliance
  | "compliance_notice_issued"
  | "compliance_warning_issued"
  | "compliance_penalty_issued"
  // Config
  | "bylaw_updated"
  | "fee_changed"
  | "config_updated"
  // Team / org
  | "member_invited"
  | "member_accepted"
  | "member_removed"
  // System
  | "registration_validated"
  | "registration_expired"
  | "subscription_activated"
  | "subscription_cancelled";

export type ActivityEntityType =
  | "host"
  | "booking"
  | "unit"
  | "document"
  | "dispute"
  | "compliance_action"
  | "invitation"
  | "organization"
  | "subscription";

// ─── Logger ───────────────────────────────────────────────────────────────────

/**
 * Insert a row into the activity_log table.
 *
 * This is a "best-effort" log — it never throws.
 * Callers should NOT await this unless they need to confirm the write.
 */
export async function logActivity(params: LogActivityParams): Promise<void> {
  const {
    orgId,
    userId = null,
    actionType,
    entityType,
    entityId = null,
    visibility = "building",
    metadata = {},
  } = params;

  if (!orgId) {
    console.warn("[activityLogger] orgId is required — skipping log");
    return;
  }

  const { error } = await supabase.from("activity_log").insert({
    org_id: orgId,
    user_id: userId,
    action_type: actionType,
    entity_type: entityType ?? null,
    entity_id: entityId,
    visibility,
    metadata,
  });

  if (error) {
    console.warn("[activityLogger] Failed to log activity:", error.message, {
      actionType,
      entityType,
      entityId,
    });
  }
}

// ─── Human-readable labels ────────────────────────────────────────────────────

export const ACTION_LABELS: Record<ActivityActionType, string> = {
  host_registered:            "Host registered",
  host_approved:              "Host approved",
  host_rejected:              "Host rejected",
  host_suspended:             "Host suspended",
  host_invited:               "Host invited",
  booking_created:            "Booking submitted",
  booking_approved:           "Booking approved",
  booking_rejected:           "Booking rejected",
  booking_blocked:            "Booking blocked",
  booking_cancelled:          "Booking cancelled",
  booking_completed:          "Booking completed",
  unit_added:                 "Unit added",
  unit_updated:               "Unit updated",
  unit_deleted:               "Unit removed",
  document_uploaded:          "Document uploaded",
  document_deleted:           "Document deleted",
  document_approved:          "Document approved",
  document_rejected:          "Document rejected",
  dispute_filed:              "Dispute filed",
  dispute_updated:            "Dispute updated",
  dispute_resolved:           "Dispute resolved",
  compliance_notice_issued:   "Notice issued",
  compliance_warning_issued:  "Warning issued",
  compliance_penalty_issued:  "Penalty issued",
  bylaw_updated:              "Bylaws updated",
  fee_changed:                "Fee changed",
  config_updated:             "Configuration updated",
  member_invited:             "Team member invited",
  member_accepted:            "Invitation accepted",
  member_removed:             "Team member removed",
  registration_validated:     "Registration validated",
  registration_expired:       "Registration expired",
  subscription_activated:     "Subscription activated",
  subscription_cancelled:     "Subscription cancelled",
};

// ─── Icon mapping (Lucide icon names) ────────────────────────────────────────

export const ACTION_ICONS: Record<ActivityActionType, string> = {
  host_registered:            "UserPlus",
  host_approved:              "CheckCircle2",
  host_rejected:              "XCircle",
  host_suspended:             "ShieldOff",
  host_invited:               "Mail",
  booking_created:            "CalendarPlus",
  booking_approved:           "CalendarCheck",
  booking_rejected:           "CalendarX",
  booking_blocked:            "CalendarX",
  booking_cancelled:          "CalendarMinus",
  booking_completed:          "CalendarCheck",
  unit_added:                 "Building2",
  unit_updated:               "Building2",
  unit_deleted:               "Trash2",
  document_uploaded:          "FileUp",
  document_deleted:           "FileX",
  document_approved:          "FileCheck",
  document_rejected:          "FileX",
  dispute_filed:              "AlertTriangle",
  dispute_updated:            "MessageSquare",
  dispute_resolved:           "CheckCircle2",
  compliance_notice_issued:   "Bell",
  compliance_warning_issued:  "AlertTriangle",
  compliance_penalty_issued:  "ShieldAlert",
  bylaw_updated:              "BookOpen",
  fee_changed:                "DollarSign",
  config_updated:             "Settings",
  member_invited:             "UserPlus",
  member_accepted:            "UserCheck",
  member_removed:             "UserMinus",
  registration_validated:     "ShieldCheck",
  registration_expired:       "ShieldOff",
  subscription_activated:     "CreditCard",
  subscription_cancelled:     "CreditCard",
};

// ─── Color mapping ────────────────────────────────────────────────────────────

export function getActivityColor(actionType: ActivityActionType): string {
  if (["host_approved","booking_approved","document_approved","dispute_resolved","registration_validated","member_accepted"].includes(actionType)) {
    return "text-green-600";
  }
  if (["host_rejected","booking_rejected","booking_blocked","document_rejected","compliance_penalty_issued"].includes(actionType)) {
    return "text-destructive";
  }
  if (["dispute_filed","compliance_warning_issued","compliance_notice_issued","booking_cancelled","host_suspended"].includes(actionType)) {
    return "text-amber-500";
  }
  return "text-primary";
}
